import de from "@/locales/de/translation.json";
import en from "@/locales/en/translation.json";
import fr from "@/locales/fr/translation.json";

import { getDefaultAliasKey } from "./productMatrix";

type DefaultNames = Record<string, string>;

const localeDefaultNames: DefaultNames[] = [
  en.translation.Onboarding.sections.checkout.summary.defaultNames,
  de.translation.Onboarding.sections.checkout.summary.defaultNames,
  fr.translation.Onboarding.sections.checkout.summary.defaultNames,
];

const defaultNamesByLanguage: Record<string, DefaultNames> = {
  en: en.translation.Onboarding.sections.checkout.summary.defaultNames,
  de: de.translation.Onboarding.sections.checkout.summary.defaultNames,
  fr: fr.translation.Onboarding.sections.checkout.summary.defaultNames,
};

/** All known onboarding default aliases across de / en / fr. */
const defaultAliasStrings: ReadonlySet<string> = new Set(
  localeDefaultNames.flatMap((names) =>
    Object.values(names).map((value) => value.trim()),
  ),
);

export const isDefaultSimAlias = (alias?: string | null): boolean => {
  const trimmed = alias?.trim();
  if (!trimmed) {
    return false;
  }

  return defaultAliasStrings.has(trimmed);
};

export const getTranslatedDefaultAlias = (
  productId: number | undefined,
  language = "en",
): string => {
  const aliasKey = getDefaultAliasKey(productId);
  if (!aliasKey.startsWith("defaultNames.")) {
    return "";
  }

  const shortKey = aliasKey.slice("defaultNames.".length);
  const languageCode = language.split("-")[0];
  const names =
    defaultNamesByLanguage[languageCode] ?? defaultNamesByLanguage.en;

  return (names[shortKey] ?? defaultNamesByLanguage.en[shortKey] ?? "").trim();
};

/**
 * After a portal tariff change: if the SIM still has an onboarding default
 * alias, return the new product's default. Custom aliases → `null` (no update).
 */
export const resolveAliasAfterTariffChange = ({
  currentAlias,
  newProductId,
  language = "en",
}: {
  currentAlias?: string | null;
  newProductId?: number;
  language?: string;
}): string | null => {
  if (!isDefaultSimAlias(currentAlias)) {
    return null;
  }

  const nextAlias = getTranslatedDefaultAlias(newProductId, language);
  if (!nextAlias || nextAlias === currentAlias?.trim()) {
    return null;
  }

  return nextAlias;
};
